import { useRef, useMemo } from "react";
import { Alert, Autocomplete, Box, Checkbox, Snackbar, TextField, useMediaQuery } from "@mui/material";
import SliderRange from "../SliderYears";
import styled from "@emotion/styled";
import { useContext, useEffect, useState } from "react";
import { FiltersContext } from "../../store/filters-options-context";
import { useParams, useLocation } from "react-router-dom";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from "@emotion/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SyrianFlag from "../../assets/Flag_of_Syria.png";
import CountryFactsheet from "./CountryFactsheet";



const CountryLabel = styled("div")(({ theme }) => ({
    display: "flex",
    fontSize: "12px",
    fontWeight: "500",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    position: "relative",
    padding: "9px 0 7px 0",
    flexDirection: 'column',
    [theme.breakpoints.up("xs")]: {
        fontSize: "11.6px",
    },
    [theme.breakpoints.up("md")]: {
        flexDirection: 'row',
        alignItems: 'center',
        fontSize: "14px",
        justifyContent: 'flex-start',
        padding: '10px 0',
        marginBottom: "8px",
    },
    [theme.breakpoints.up("lg")]: {
        padding: "0",
        fontSize: "18px",
        letterSpacing: '-0.2pt'
    },
}));

const SliderRangeContainer = styled("div")(({ theme, display }) => ({
    position: "relative",
    flex: "1 1 auto",
    width: "93%",
    marginLeft: "4%",
    display: display || 'block',
    [theme.breakpoints.up("lg")]: {
        width: "99%",
        marginLeft: "1%",
    },
    "&:before": {
        display: "none",
    },
}));

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function HeaderFixed({ country, params, setParams, yearRange }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    let { type } = useParams();
    const location = useLocation();
    if (!type) {
        const parts = location.pathname.split('/').filter(Boolean);
        const first = (parts[0] || '').toLowerCase();
        const second = (parts[1] || '').toLowerCase();
        type = (first === 'files' ? second : first) || undefined;
    }
    const { mainCategory, areaGroups, setRegions, countryCode } = useContext(FiltersContext);

    // Derive comparingCountries from params.area and areaGroups
    const comparingCountries = useMemo(() => {
        if (!params?.area || !Array.isArray(params.area) || !areaGroups || areaGroups.length === 0) {
            return [];
        }

        // Skip the first element (main country) and get comparison countries
        const comparisonCodes = params.area.slice(1);

        // Map codes to area group objects
        return comparisonCodes
            .map(code => areaGroups.find(area => area.code === code))
            .filter(Boolean); // Remove any undefined values
    }, [params?.area, areaGroups]);

    const [counter, setCounter] = useState(0);
    const [toggle, setToggle] = useState(!isMobile);
    const [compareCount, setCompareCount] = useState({
        open: false,
        vertical: "top",
        horizontal: "center",
    });

    const { vertical, horizontal, open } = compareCount;
    const headerRef = useRef(null);


    const handleClose = () => {
        setCompareCount(prev => ({ ...prev, open: false }));
    };

    const toggleFilter = () => {
        setToggle((prevValue) => !prevValue);
    };

    useEffect(() => {
        setToggle(!isMobile);
    }, [isMobile]);

    // Update counter when comparingCountries changes
    useEffect(() => {
        setCounter(comparingCountries.length);
    }, [comparingCountries]);

    useEffect(() => {
        if (!isMobile) return;

        const handleClickOutside = (event) => {
            const clickedInsideHeader =
                headerRef.current && headerRef.current.contains(event.target);

            const autocompletePopup = document.querySelector(
                ".MuiAutocomplete-popper"
            );
            const clickedInsideAutocomplete =
                autocompletePopup && autocompletePopup.contains(event.target);

            if (!clickedInsideHeader && !clickedInsideAutocomplete) {
                setToggle(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMobile]);

    const compareSubregionCountries = (code, isChecked, label) => {
        setRegions((prevParams) => {
            const regionExists = prevParams.some((region) => region.code === code);

            if (isChecked && !regionExists) {
                return [
                    ...prevParams,
                    { code: code, label: label, subtype: "toKeepOnChart" },
                ];
            } else if (!isChecked && regionExists) {
                return prevParams.filter((region) => region.code !== code);
            }

            return prevParams;
        });

        setParams((prevParams) => {
            const prevArea = prevParams.area || [];
            let updatedItems;

            if (isChecked) {
                updatedItems = [...prevArea, code];
            } else {
                updatedItems = prevArea.filter((item) => item !== code);
            }

            const mainId = params?.area?.[0] ?? countryCode;
            const mainIdNum = typeof mainId === 'string' ? Number(mainId) : mainId;
            const filteredItems = updatedItems.filter(item => item !== Number(mainIdNum));
            const newArea = filteredItems.includes(mainIdNum) ? filteredItems : [mainIdNum, ...filteredItems];


            return {
                ...prevParams,
                area: newArea
            };
        });
    };


    return (
        <Box
            sx={{
                flexDirection: { xs: "column", alignItems: "flex-start" },
                backgroundColor: "secondary.main",
            }}
            id="fixed-header"
            alignItems="center"
            ref={headerRef}
        >
            <Box display="flex" width="100%">
                {country && type !== "bulk" && (
                    <CountryLabel className="country-label">
                        <Box
                            className="country-box"
                            sx={{
                                width: {
                                    xs: "100%",
                                    md: "auto",
                                    fontWeight: "600",
                                },
                            }}
                        >
                            <CountryFactsheet country={country} />
                            <Box
                                component="img"
                                loading="lazy"
                                width="auto"
                                srcSet={
                                    country.code.toLowerCase() === "sy"
                                        ? `${SyrianFlag} 2x`
                                        : `https://flagcdn.com/w80/${country.code.toLowerCase()}.png 2x`
                                }
                                src={
                                    country.code.toLowerCase() === "sy"
                                        ? `${SyrianFlag}`
                                        : `https://flagcdn.com/w80/${country.code.toLowerCase()}.png`
                                }
                                alt=""
                                sx={{ mr: { xs: "7px", md: "15px" } }}
                            />
                            {country.label}
                            {mainCategory && mainCategory === "indicators" && (
                                <>
                                    <Box
                                        sx={{
                                            fontSize: "9.6px",
                                            flex: 1,
                                            display: isMobile ? "block" : "none",
                                            mt: "2px",
                                        }}
                                    >
                                        &nbsp;&nbsp;&nbsp;&nbsp;{params?.year[0]} -{" "}
                                        {params?.year.slice(-1).pop()}
                                    </Box>
                                    <Box
                                        onClick={toggleFilter}
                                        sx={{
                                            ml: "auto",
                                            cursor: "pointer",
                                            display: { xs: "flex", md: "none" },
                                        }}
                                    >
                                        {toggle ? <CloseIcon /> : <FilterListIcon />}
                                    </Box>
                                </>
                            )}
                        </Box>
                        {mainCategory && mainCategory === "indicators" && (
                            <Box
                                flexDirection="row"
                                display="flex"
                                alignItems="center"
                                sx={{
                                    position: "relative",
                                    [theme.breakpoints.up("md")]: {
                                        marginLeft: "auto",
                                    },
                                    "&:before": {
                                        content: '""',
                                        width: "1px",
                                        height: "80%",
                                        backgroundColor: theme.palette.secondary.dark,
                                        display: "block",
                                        left: "-1.5vw",
                                        position: "absolute",
                                        [theme.breakpoints.up("md")]: {
                                            content: '""',
                                        },
                                    },
                                }}
                            >
                                <Autocomplete
                                    multiple
                                    id="subregions"
                                    value={comparingCountries}
                                    getOptionDisabled={(option) => counter >= 3}
                                    options={areaGroups || []}
                                    popupIcon={<ExpandMoreIcon />}
                                    disableCloseOnSelect
                                    disableClearable
                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                    onOpen={() => {
                                        // Show alert when dropdown opens with 3+ countries already selected
                                        if (counter >= 3) {
                                            setCompareCount(prev => ({ ...prev, open: true }));
                                        }
                                    }}
                                    onClose={() => {
                                        // Close the alert when dropdown closes
                                        setCompareCount(prev => ({ ...prev, open: false }));
                                    }}
                                    onChange={(event, value, reason, details) => {
                                        // Don't need setComparingCountries anymore - it's derived from params.area
                                        if (details && details.option) {
                                            const selectedCode = details.option.code;
                                            const selectedLabel = details.option.label;
                                            const isChecked = value.some(
                                                (option) => option.code === selectedCode
                                            );
                                            compareSubregionCountries(
                                                selectedCode,
                                                isChecked,
                                                selectedLabel
                                            );
                                        }
                                    }}
                                    getOptionLabel={(option) => option.label}
                                    renderOption={(props, option, { selected }) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={key} {...optionProps}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option.label}
                                            </li>
                                        );
                                    }}
                                    sx={{
                                        width: { xs: "93vw", md: "30vw", lg: "30vw" },
                                        marginTop: { xs: "15px", md: "0" },
                                        display: toggle ? "flex" : "none",
                                        maxWidth: "420px",
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            sx={{
                                                "& .MuiInputBase-input::placeholder": {
                                                    fontSize: "8px",
                                                    color: theme.palette.primary.fontColor,
                                                    opacity: "0.7",
                                                    fontStyle: "italic",
                                                },
                                                "@media (min-width: 1200px)": {
                                                    "& .MuiInputBase-input::placeholder": {
                                                        fontSize: "10px",
                                                    },
                                                },
                                                "@media (min-width: 1366px)": {
                                                    "& .MuiInputBase-input::placeholder": {
                                                        fontSize: "11px",
                                                    },
                                                },
                                            }}
                                            placeholder="Select up to three countries in the subregion to compare (line charts only)"
                                        />
                                    )}
                                />
                            </Box>
                        )}
                        {counter >= 3 && (
                            <Snackbar
                                anchorOrigin={{ vertical, horizontal }}
                                open={open}
                                onClose={handleClose}
                                autoHideDuration={5000}
                                key={vertical + horizontal}
                            >
                                <Alert severity="error">
                                    You can compare max. {counter} countries.
                                </Alert>
                            </Snackbar>
                        )}
                    </CountryLabel>
                )}
            </Box>
            {mainCategory && mainCategory !== "repository" && (
                <SliderRangeContainer
                    display={type === "bulk" ? "block" : toggle ? "block" : "none"}
                >
                    <SliderRange
                        params={params}
                        setParams={setParams}
                        yearRange={yearRange}
                        isMobile={isMobile}
                    />
                </SliderRangeContainer>
            )}
        </Box>
    );
}
